/**
 * Design tokens — the single source of truth. Tailwind reads these through the
 * MUI CSS variables in src/styles/index.css, so a change here reaches both.
 */

/** A true neutral grey ramp. The app is deliberately achromatic: chrome,
 *  surfaces, nav and filled controls are all grey, and the only hue on screen
 *  belongs to status — an overdue item's red, a paid one's green. That is the
 *  point. In a portal whose whole job is telling a director what is late and
 *  what is settled, colour is information, and spending it on decoration is
 *  what makes a palette compete with the red it sits beside.
 *
 *  NEUTRAL, with no colour temperature at all, and that is load-bearing rather
 *  than a default. The portal is white-labelled across tenants: an agent's
 *  logo arrives as an arbitrary colour, and a grey with a cast of its own
 *  reads as a second brand arguing with it. The previous ramp was blue-slate,
 *  which sat well behind a blue mark and badly behind a warm one. A true grey
 *  is the only neutral that stays neutral behind ANY tenant.
 *
 *  The low rungs are deliberately LIGHT and close together: they are the
 *  working surfaces — panels, nav hovers, tiles, card headers — and keeping
 *  them pale is what makes the screen read as calm and uncrowded rather than
 *  as a dense console. The high rungs (700–950) are ink and fill, where
 *  contrast has to hold: they stay heavy precisely so that the few dark things
 *  on a pale screen are the things that matter. */
export const brand = {
  50: '#fbfbfb',
  100: '#f6f6f6',
  200: '#ececec',
  300: '#dcdcdc',
  400: '#b0b0b0',
  500: '#7c7c7c',
  600: '#575757',
  700: '#454545',
  800: '#2e2e2e',
  900: '#1a1a1a',
  950: '#0d0d0d',
} as const;

/** The single saturated colour in the chrome, reserved for IDENTITY — the
 *  account avatar and nothing else.
 *
 *  It is deliberately outside the greyscale system rather than a rung of it.
 *  The app spends colour on status (late, settled, needs attention) and on
 *  nothing else, but "whose account is this" is a question a grey circle of
 *  initials cannot answer at a glance. One reserved hue, one job. Adding a
 *  second use for it is what would start the drift back to a coloured UI.
 *
 *  Deep and desaturated rather than a bright blue: on a true-neutral ground a
 *  vivid accent is the one thing that would break the white-label promise, and
 *  this reads as ink that happens to carry a hue. It also stays clear of the
 *  three status colours, so an avatar is never mistaken for a state. */
export const accent = '#3f4c66';

/** The pale gradient the app uses on a panel — every DetailCard header. The
 *  Outstanding hero used to carry it too and now takes `heroGradient` below.
 *
 *  It runs light to dark and ENDS on the darker stop, so a panel resolves
 *  into its own edge rather than fading out. The stops are deliberately pale
 *  and close together — the panel is a SURFACE, something to read, and the
 *  gradient is there to give it presence without weight.
 *
 *  That is the app's whole visual grammar: pale means information, dark means
 *  something to press. Everything painted with this carries dark ink; the few
 *  dark objects on screen are the actions. Keeping the split honest is what
 *  lets a reader sort a screen before reading a word of it. */
export const panelGradient = {
  from: '#f7f7f7',
  via: '#efefef',
  to: '#dedede',
} as const;

/** The dark gradient the Outstanding hero is painted with.
 *
 *  The hero is the one exception to "pale means information, dark means
 *  something to press", and it earns it by being the screen's subject rather
 *  than one card among several: it states the amount owed, and everything
 *  below it is a breakdown of that figure. Inverting it is what separates the
 *  answer from the working, on a page where every other surface is pale.
 *
 *  The MIDDLE stop is the light one, so the panel is crossed by a diagonal
 *  band of light with both corners falling away dark — the way a sheet of
 *  metal catches a highlight along its length. A plain dark-to-darker fade
 *  reads as a flat plane that happens to be uneven; the band gives the
 *  surface a direction and a light source, and it is what makes the hero the
 *  one object on the page that looks lit rather than filled.
 *
 *  The dark ends are rung 800, not the bottom of the ramp. Near-black corners
 *  crushed the falloff into a hard edge and made the panel read as two flat
 *  halves with a seam; 800 is the same ink the filled buttons use, so the
 *  corners stay recognisably part of the palette while the band still has
 *  somewhere to travel.
 *
 *  It carries white ink, and the hero's own action inverts with it — a dark
 *  button on a dark panel would disappear, so the button goes pale there and
 *  nowhere else. */
export const heroGradient = {
  from: brand[800],
  via: brand[400],
  to: brand[800],
} as const;

export const palette = {
  primary: {
    // Rung 800: with no brand hue to spend, "primary" is carried by WEIGHT.
    // The filled button is the darkest, heaviest object on a pale screen
    // because it is the one thing to press, and at 14.6:1 under white text it
    // is unmistakable as a control at a glance.
    //
    // That "at a glance" is the whole requirement. The reader is an accountant
    // working down a list of obligations, not someone studying a page: they
    // need to find the action without reading the screen. A pale fill — tried
    // and reverted — cannot do that, because it has barely more luminance than
    // the card behind it and has to be hunted for.
    //
    // The pale panel gradient is deliberately NOT used here. It is SURFACE;
    // this is ACTION. Pale means information, dark means something to press,
    // and that split is what lets the eye sort a screen before reading it.
    main: brand[800],
    light: brand[600],
    dark: brand[900],
    contrastText: '#ffffff',
  },
  secondary: {
    // The mid rung, for the quieter of two filled controls sitting together.
    main: brand[500],
    light: brand[400],
    dark: brand[700],
    contrastText: '#ffffff',
  },
  // The ONLY hue in the app, and therefore the only thing colour is asked to
  // mean: late, settled, needs attention. Each clears AA against white
  // (5.47 / 5.00 / 4.75), and against a grey chrome they now carry far more
  // signal than they did competing with a blue palette.
  success: { main: '#1a7f52', light: '#3fa877', dark: '#115638' },
  warning: { main: '#a8620a', light: '#cf8226', dark: '#754209' },
  error: { main: '#c1362f', light: '#d95b55', dark: '#88231e' },
  // Informational panels are chrome, not status — grey, so they never read as
  // one of the three states above.
  info: { main: brand[600], light: brand[400], dark: brand[800] },
} as const;

/** Status-tag colours, shared with the CleanID (kyc-fe) console.
 *
 *  These are NOT the `palette.success/warning/error` values above, and that is
 *  deliberate. Those three are the app's own restrained status hue, sized to sit
 *  quietly on a grey chrome — a row rail, a late date, an icon tile. A status
 *  TAG has the opposite job: it is read across a table at a glance, and the
 *  muted ramp made "Paid", "Awaiting Signature" and "In Progress" resolve into
 *  three near-identical off-whites separated only by their icon.
 *
 *  So the tag borrows CleanID's palette — one director sees both products, and
 *  a status that is blue in one and grey in the other is read as two different
 *  states. Each entry is `fill` / `ink` / `edge` from Tailwind's 100 / 700 / 200
 *  rungs, the shape CleanID's `KycStatusTags` uses.
 *
 *  The ONE departure: CleanID inks at rung 600, which on its own 100 fill gives
 *  2.7:1 for amber, 3.0:1 for green and 4.0:1 for red — below AA for text this
 *  small, and these chips ARE text (§11). Rung 700 is the same hue at the same
 *  fill and clears 4.5:1 across all five. The tags read as the same component
 *  in both products; only the ink is a shade deeper.
 *
 *  `inkStrong` (rung 800) exists only on the two hues the pay/sign buttons use.
 *  Those buttons darken their INK on hover rather than deepening their fill:
 *  moving the fill to `edge` would drop rung-700 ink to 4.1-4.2:1, under AA, so
 *  the hover state would fail the check the rest state passes. */
export const statusTag = {
  // gray — nothing is being asked of anyone yet.
  neutral: { fill: '#f3f4f6', ink: '#4b5563', edge: '#e5e7eb' },
  // blue — under way, but not waiting on this director.
  progress: { fill: '#dbeafe', ink: '#1d4ed8', edge: '#bfdbfe' },
  // yellow — the director has something to do. CleanID's own yellow, so
  // "needs you" is the same colour in both products.
  warning: { fill: '#fef9c3', ink: '#a16207', edge: '#fef08a', inkStrong: '#854d0e' },
  // green — settled.
  success: { fill: '#dcfce7', ink: '#15803d', edge: '#bbf7d0', inkStrong: '#166534' },
  // red — overdue, rejected, cancelled.
  danger: { fill: '#fee2e2', ink: '#b91c1c', edge: '#fecaca' },
} as const;

/** Role-chip colours, in the same fill / ink / edge shape as `statusTag`.
 *
 *  Roles used to borrow `primary` / `success` / `warning` — chosen, by the old
 *  comment's own admission, because they were the palette entries left over —
 *  which put Secretary in the same green as "Paid" and Shareholder in the same
 *  amber as "Awaiting signature".
 *
 *  Director blue and Shareholder green are CleanID's, taken from the initials
 *  avatars it draws beside each name in the group lookup step (blue 100/700 and
 *  green 100/700). CleanID has no Secretary — it does not model that role — so
 *  purple is this app's own addition, picked to sit apart from both at a glance
 *  without landing on the amber or red a status already owns.
 *
 *  Note these DO overlap the status hues: blue is also `progress`, green also
 *  `success`. That is acceptable because a role chip and a status chip never
 *  carry the same word, and matching CleanID matters more here than reserving
 *  the hue — a director sees both products. */
export const roleTag = {
  // blue — the role this portal exists to serve, so it leads.
  Director: { fill: '#dbeafe', ink: '#1d4ed8', edge: '#bfdbfe' },
  // purple — no CleanID equivalent; chosen to read apart from blue and green.
  Secretary: { fill: '#f3e8ff', ink: '#7e22ce', edge: '#e9d5ff' },
  // green.
  Shareholder: { fill: '#dcfce7', ink: '#15803d', edge: '#bbf7d0' },
} as const;

/** `subtle` and `default` are the same rung deliberately: the sidebar and the
 *  canvas are one continuous plane, and the nav is separated from the content
 *  by its border alone. Two near-identical greys touching along an edge read as
 *  a printing error rather than as structure.
 *
 *  Drawn from the same slate ramp as everything else, so there is exactly one
 *  neutral in the app. A second grey mixed in — even a close one — is what
 *  makes chrome look assembled from parts. */
export const surfaces = {
  background: { default: brand[50], paper: '#ffffff', subtle: brand[50] },
  text: {
    // 900 for ink and 600 for muted: 17.9:1 and 8.6:1 on white.
    //
    // Muted is rung 600 rather than 500 deliberately. 500 passes AA at 4.8:1,
    // but this rung carries due dates, item subtitles and the nav's inactive
    // rows — text that is scanned rather than studied, and what the reader is
    // scanning FOR is often exactly what is written in it. "78 days late" is
    // not decoration. Clearing 7:1 costs nothing in layout and buys back the
    // legibility a "muted" style usually spends.
    primary: brand[900],
    secondary: brand[600],
    disabled: brand[400],
  },
  divider: 'rgba(26, 26, 26, 0.12)',
} as const;

/** Cast from the same ink as `surfaces.text.primary`, so a palette change
 *  carries through to elevation instead of leaving a stale grey behind.
 *
 *  `card` is deliberately stronger than a hairline border alone: white content
 *  on the near-white canvas (slate 50) needs a real edge to separate, and at 8%
 *  the divider on its own leaves the card floating. */
export const shadow = {
  card: '0 1px 2px rgb(26 26 26 / 0.05), 0 4px 12px -2px rgb(26 26 26 / 0.09)',
  menu: '0 6px 16px -4px rgb(26 26 26 / 0.11), 0 2px 6px -2px rgb(26 26 26 / 0.07)',
  /** For the one panel that has to sit forward of the canvas. A pale fill on a
   *  pale ground has almost no luminance separation of its own (~1.1:1), so the
   *  elevation is what makes it read as a surface rather than a colour patch.
   *
   *  Three layers, because a single offset shadow only darkens the bottom edge
   *  and left the panel looking pasted on along its top and sides. The first
   *  has no Y offset at all — it is the ambient ring that closes the shadow
   *  around all four borders; the other two carry the drop and the depth. */
  raised: [
    '0 0 6px 0 rgb(26 26 26 / 0.11)',
    '0 3px 6px -1px rgb(26 26 26 / 0.11)',
    '0 16px 32px -8px rgb(26 26 26 / 0.18)',
  ].join(', '),
} as const;

/** Three options, not a spectrum. A codebase with nine radii looks unowned. */
export const radius = {
  sm: 6,
  md: 8,
  lg: 12,
  pill: 999,
} as const;

/** 44px is the smallest reliably tappable size on a phone. Every interactive
 *  element must meet it. */
export const MIN_TOUCH_TARGET = 44;

/** Mirrored into Tailwind in src/styles/index.css. Change both or neither. */
export const breakpoints = {
  xs: 0,
  sm: 600,
  md: 900,
  lg: 1200,
  xl: 1536,
} as const;
